import { BreakpointObserver } from '@angular/cdk/layout';
import {
  ChangeDetectorRef,
  Component,
  Inject,
  LOCALE_ID,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import * as URI from 'urijs';

import { selectTenant, State } from '®app/ngrx/state';
import { User } from '®shared/auth';
import { AppConfig } from '®shared/config';
import { CurrentUserService } from '®shared/current-user';
import {
  createDefaultUserPermissions,
  Permissions,
  UserPermissions,
} from '®shared/permissions';
import { RedirectionService } from '®shared/redirection';
import { Tenant } from '®shared/tenant';
import { UriService } from '®shared/url-utils';

export const MEDIUM_SCREEN_BREAKPOINT = '(min-width: 768px)';

@Component({
  selector: 'rl-navbar',
  templateUrl: './navbar.component.html',
})
export class NavbarComponent implements OnInit, OnDestroy {
  isCollapsed: boolean;
  tenant$: Observable<Tenant | null>;
  permissions: UserPermissions;
  user: User | null = null;

  tenantName = this.appConfig.tenant.name;
  reviewsUrl = this.appConfig.tenant.reviewsUrl;

  private breakpointSubscription: Subscription;
  private currentUserSubscription: Subscription;

  constructor(
    private appConfig: AppConfig,
    private currentUserService: CurrentUserService,
    private breakpointObserver: BreakpointObserver,
    private cd: ChangeDetectorRef,
    @Inject(LOCALE_ID) public locale: string,
    private redirectionService: RedirectionService,
    private uriService: UriService,
    private store: Store<State>,
  ) {
    this.permissions = createDefaultUserPermissions(
      appConfig.tenant.roleCodesByRoleId,
    );
  }

  ngOnDestroy() {
    if (this.currentUserSubscription) {
      this.currentUserSubscription.unsubscribe();
    }
    if (this.breakpointSubscription) {
      this.breakpointSubscription.unsubscribe();
    }
  }

  ngOnInit() {
    this.tenant$ = this.store.select(selectTenant);

    this.currentUserSubscription = this.currentUserService
      .getCurrentUser()
      .subscribe(currentUser => {
        this.user = currentUser.oauthUser;
        this.permissions = currentUser.permissions;
      });

    this.breakpointSubscription = this.breakpointObserver
      .observe([MEDIUM_SCREEN_BREAKPOINT])
      .subscribe(result => {
        this.isCollapsed = !result.matches;
        this.cd.markForCheck();
      });
  }

  /**
   * Wrapper method to generate a classic TARL URL, with appropriate flags to log a user in correctly
   * @param {string} url Relative URL
   */
  generateClassicUrl(url: string = '/index.html'): string {
    return this.uriService.getClassicUri(new URI(url)).toString();
  }

  /**
   * Wrapper method to generate a URL for switching the language of the page, by appending a lang= param
   * @param {string} language The language being switched to
   */
  generateSwitchLanguageUrl(language: string): string {
    return this.uriService
      .getCurrentUri()
      .setSearch('lang', language)
      .toString();
  }

  showAdminMenu(): boolean {
    return this.permissions.hasGlobalPermission(
      Permissions.ACCESS_KEYS,
      Permissions.ADMIN_BRANDING,
      Permissions.ADVANCED_MIS,
      Permissions.GRANT_ANY_ROLE,
      Permissions.HOME_MESSAGE,
      Permissions.IDP_MIGRATION,
      Permissions.REQUEST_ROLLOVER,
      Permissions.RESOURCE_METADATA_REFRESH,
      Permissions.UPLOAD_HIERARCHY,
      Permissions.VIEW_ACQUISITIONS,
    );
  }

  canReviewJobs(): boolean {
    return this.permissions.hasGlobalPermission(
      Permissions.REQUEST_ROLLOVER,
      Permissions.RESOURCE_METADATA_REFRESH,
      Permissions.UPLOAD_HIERARCHY,
    );
  }

  showMisDashboardLink(): boolean {
    return (
      this.permissions.hasGlobalPermission(Permissions.ADVANCED_MIS_VIEW) &&
      this.appConfig.tenant.featureFlags.isMisDashboardEnabled
    );
  }

  /**
   * Toggles the isCollapsed state of the navbar
   **/
  onToggle() {
    this.isCollapsed = !this.isCollapsed;
  }

  /**
   * Log the user in.
   */
  login(): void {
    this.currentUserService.login();
  }

  /**
   * Log the user out, both of Shipshape and RL.
   *
   * The RL logout page has a redirect-to-index behaviour that is desirable here.
   */
  logout(): void {
    this.currentUserService.logout();

    const rlRedirectUrl = new URI(this.appConfig.tenant.httpsBaseUri)
      .segment('/logout.html')
      .toString();

    const logoutUrl = new URI(this.appConfig.personaEndpoint)
      .segment('/auth/logout')
      .query({
        redirectUri: rlRedirectUrl,
      })
      .toString();

    this.redirectionService.redirectToExternalUrl(logoutUrl);
  }
}
